/**
 * Redirection vers /login sur 401.
 *
 * Vit dans `helpers/` et non dans `components/router/` à cause de la frontière du
 * socle mobile (#23) : `api/axios-client.ts`, `api/queryClient.ts` et
 * `contexts/ContextProvider.tsx` en dépendent, et ces trois-là sont des zones que
 * le mobile importe. Un helper qui remonte dans `components/` ferait fuir tout le
 * bundle mobile dans l'arbre du bureau. Module feuille, comme `domainSlug.ts`.
 */
import { getDomainSlug } from "./domainSlug";

// Routes publiques qui ne doivent jamais déclencher une redirection vers /login
// (le guest n'est pas censé avoir un token Sanctum, donc un 401 sur un fetch
// background — ex: /user du ContextProvider — ne doit pas l'éjecter de la page)
//
// Deux pages hors layout en font partie : `sso` (le visiteur arrive sans token
// avec un code SSO à usage unique, qu'il doit pouvoir échanger avant toute
// redirection) et `chat` (lien envoyé par e-mail au visiteur du chat).
//
// Exporté pour le canari `__tests__/publicRoutesCanary.test.ts`, qui vérifie que
// cette liste couvre bien les routes publiques de router.tsx, dans les layouts
// publics comme hors layout : une route publique oubliée ici éjecte le guest,
// sans erreur visible en dev où l'on est presque toujours authentifié.
export const AUTH_PUBLIC_ROUTES = ["login", "signup", "forgot-password", "reset-password", "survey-response", "opt-out", "opt-out-phone", "locks", "calendar", "calendar-custom", "wallet", "scan", "kiosk", "public-sales-view", "public-occupants-view", "public-booking-request", "salon", "salon-guides", "salon-equipe", "salon-diagnostic", "salon-prestataire", "webinaire", "formulaire", "payment", "sso", "chat"];
const authPublicRegex = new RegExp(`^/(?:${AUTH_PUBLIC_ROUTES.join("|")})(?:/.*)?$`);

/**
 * Un chemin fait-il partie des routes publiques (pas d'auth requise) ?
 *
 * Source unique de vérité pour tout code qui a besoin de savoir, AVANT
 * d'appeler `redirectToLogin()`, si la page courante est publique — ex:
 * `ContextProvider` qui décide s'il doit même déclencher un fetch de session.
 * Ne pas dupliquer cette liste ailleurs : c'est exactement le piège qui a
 * laissé `/wallet` cassée pour les guests un sprint entier.
 */
export function isAuthPublicPath(pathname: string): boolean {
    return authPublicRegex.test(pathname);
}

export function redirectToLogin(preservePath = true) {
    const { pathname, search } = window.location;

    // 1) Ne jamais rediriger si on est déjà sur une page d'auth publique (/login, /signup, ... ou avec slug)
    if (authPublicRegex.test(pathname)) return;

    // 2) Évite les boucles si un redirect est déjà présent
    if (/\bredirect=/.test(search)) return;

    // 3) Construit la cible à préserver
    const next = preservePath ? encodeURIComponent(pathname + search) : "";

    // 4) Détermine un éventuel slug de domaine
    const domainSlug = getDomainSlug(); // null si localhost:3000 ou app.trigger-flow.com

    // 5) Redirection propre
    if (domainSlug) {
        window.location.href = next
            ? `/login/${encodeURIComponent(domainSlug)}?redirect=${next}`
            : `/login/${encodeURIComponent(domainSlug)}`;
    } else {
        window.location.href = next ? `/login?redirect=${next}` : `/login`;
    }
}
